import { useState } from 'react';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import { queryKeys } from '../../../queries/keys';
import type { TextOperationRequest, TextOperationResult } from '../../../domain';
import { TextOperationFailedError } from './textOperationFailedError';
import { useStudioRequest } from './studioRequestContext';
import { dispatchAiInsert } from '../canvas/plugins/AIInsertPlugin';
import { readSelectedModel } from '../../../store/agentModelStore';
import { useRequireSignIn } from '../../../components/auth/useRequireSignIn';
import {
  SignInRequiredError,
  submitErrorMessage,
} from '../../../components/auth/signInRequiredError';

type DecoratableRequest = {
  sessionId?: string;
  studioId?: string;
  metadata?: Record<string, unknown>;
  model?: string;
};

/**
 * Text-operation mutation. Mirrors Image Lab's useOperationMutation:
 *  - injects the active studio's `studioId` + selection-derived `metadata`
 *    from StudioRequestContext (a value already on the request wins);
 *  - promotes a BE-reported `status: Failed`/`Cancelled` into a thrown
 *    TextOperationFailedError so `mutation.error` surfaces it;
 *  - invalidates session + history + assets caches so the gallery + lineage
 *    reflect the new draft. Reads sessionId from variables (lazy-session safe).
 */
export function useTextOperationMutation<TRequest extends DecoratableRequest>(
  mutationFn: (request: TRequest) => Promise<TextOperationResult>,
): UseMutationResult<TextOperationResult, Error, TRequest> {
  const qc = useQueryClient();
  const { requireSignIn } = useRequireSignIn();
  const studioRequest = useStudioRequest();
  return useMutation<TextOperationResult, Error, TRequest>({
    mutationFn: async (request: TRequest) => {
      // Panels normally hit the gate earlier, in ensureSession; this covers any
      // caller that submits without creating a session first.
      if (!requireSignIn()) throw new SignInRequiredError();

      // Inject ambient context the panels don't have to thread by hand:
      //   • studioId + metadata from StudioRequestContext (Slice 6 / G1)
      //   • model from the AgentBar's persisted selection (Path 2 — the
      //     user pick made in the AgentBar applies to every text op).
      // A value already on the request wins (allows per-call overrides
      // in tests / future advanced UI).
      const selectedModel = readSelectedModel();
      const decorated = {
        ...request,
        ...(request.studioId === undefined && studioRequest.studioId !== undefined
          ? { studioId: studioRequest.studioId }
          : {}),
        ...(request.metadata === undefined && studioRequest.metadata !== undefined
          ? { metadata: studioRequest.metadata }
          : {}),
        ...(request.model === undefined && selectedModel ? { model: selectedModel } : {}),
      } as TRequest;
      const result = await mutationFn(decorated);
      if (result.status === 'Failed' || result.status === 'Cancelled') {
        throw new TextOperationFailedError(result);
      }
      return result;
    },
    onSuccess: (_data, variables) => {
      const sid = variables.sessionId;
      if (sid) {
        qc.invalidateQueries({ queryKey: queryKeys.session(sid) });
        qc.invalidateQueries({ queryKey: queryKeys.history(sid) });
        qc.invalidateQueries({ queryKey: queryKeys.sessionAssets(sid) });
      }
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

interface RunOptions {
  /** Splice the result into the editor on success (default true; Critique sets false). */
  insert?: boolean;
  onResult?: (result: TextOperationResult) => void;
}

/**
 * Higher-level panel helper: ties ensureSession → build → mutate → AI-insert →
 * onResult, capturing validation/build errors. Each panel supplies a `build`
 * that closes over its form state and receives the resolved sessionId. Must run
 * inside the WriterLab <LexicalComposer> (it reads the editor via context).
 */
export function useRunTextOperation<TRequest extends TextOperationRequest>(
  mutationFn: (request: TRequest) => Promise<TextOperationResult>,
  ensureSession: () => Promise<string>,
  documentId: string | null,
  options: RunOptions = {},
) {
  const [editor] = useLexicalComposerContext();
  const mutation = useTextOperationMutation(mutationFn);
  const [validationError, setValidationError] = useState<string | null>(null);

  const run = async (build: (sessionId: string, documentId: string | null) => TRequest) => {
    try {
      const sid = await ensureSession();
      const request = build(sid, documentId);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      if (options.insert !== false) {
        dispatchAiInsert(editor, result, request.scope);
      }
      options.onResult?.(result);
      return result;
    } catch (err) {
      setValidationError(submitErrorMessage(err));
      return undefined;
    }
  };

  return { run, mutation, validationError };
}
